import { useRefDataMap } from '@/api/hooks/useRefDataMap'
import type { CrossroadsMappingType } from '@/api/types'
import { BBDTag } from '@gravitate-js/excalibrr'
import { Popover } from 'antd'
import { useMemo } from 'react'

export interface RefDataSourceIdsCellProps {
  sourceIds: string[] | null
  sourceSystem: string | undefined
  mappingType: CrossroadsMappingType
  popoverTitle: string
  maxVisible?: number
}

export function RefDataSourceIdsCell({
  sourceIds,
  sourceSystem,
  mappingType,
  popoverTitle,
  maxVisible = 2,
}: RefDataSourceIdsCellProps) {
  const { data: entries } = useRefDataMap(sourceSystem, mappingType)

  const nameMap = useMemo(() => {
    const map = new Map<string, string>()
    if (entries) {
      for (const entry of entries) {
        map.set(entry.source_id, entry.display_name)
      }
    }
    return map
  }, [entries])

  if (!sourceIds || sourceIds.length === 0) {
    // Blank reads as "settable, currently not set"
    return null
  }

  const getDisplayText = (sourceId: string): string => {
    const name = nameMap.get(sourceId)
    return name ? `${name} (${sourceId})` : sourceId
  }

  const renderTag = (sourceId: string, index: number) => (
    <BBDTag key={index} theme2 style={{ margin: 0 }}>
      {getDisplayText(sourceId)}
    </BBDTag>
  )

  const visibleIds = sourceIds.slice(0, maxVisible)
  const overflowCount = sourceIds.length - maxVisible
  const hasOverflow = overflowCount > 0

  const popoverContent = (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        maxWidth: '400px',
        maxHeight: '300px',
        overflowY: 'auto',
      }}
    >
      {sourceIds.map((id, index) => renderTag(id, index))}
    </div>
  )

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        alignItems: 'center',
        padding: '4px 0',
      }}
    >
      {visibleIds.map((id, index) => renderTag(id, index))}
      {hasOverflow && (
        <Popover
          content={popoverContent}
          title={popoverTitle}
          trigger='click'
          placement='bottom'
        >
          <BBDTag
            theme2
            style={{
              margin: 0,
              cursor: 'pointer',
              borderStyle: 'dashed',
            }}
          >
            +{overflowCount} more
          </BBDTag>
        </Popover>
      )}
    </div>
  )
}
